import * as THREE from 'three'
import type { GLTF, GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import type { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js'
import { estimateThreeTextureBytes } from '@/plugins/mesh/loaders/gltfLowMemory/estimateTextureBytes'
import { Ktx2RequestLoader } from '@/plugins/mesh/loaders/Ktx2RequestLoader'
import type { IMeshKtx2Service } from '@/plugins/mesh/loaders/MeshKtx2Service'
import type { MeshTextureTaskPool } from '@/plugins/mesh/loaders/MeshTextureTaskPool'
import { enumerateMaterialTextures } from '@/plugins/mesh/MeshResourceTracker'
import type { MeshKtx2Report, MeshLoadRequest } from '@/plugins/mesh/types'
import { getLoggerManager } from '@/shared/logging'

const EXTENSION = 'KHR_texture_basisu'
const log = getLoggerManager().getLogger('mesh:gltf:ktx2')

/** 保留 Three.js sampler 和材质管线，仅适配其图片读取及颜色空间共享契约。 */
export function installMeshKtx2(
  loader: GLTFLoader,
  request: MeshLoadRequest,
  pool: MeshTextureTaskPool,
  service: IMeshKtx2Service | undefined,
  onError: (error: unknown) => void
): {
  dispose(): void
  wasDisposed(texture: THREE.Texture): boolean
  getReport(): MeshKtx2Report | undefined
} {
  let adapter: Ktx2RequestLoader | undefined
  let report: MeshKtx2Report | undefined
  loader.register((parser) => {
    adapter = service
      ? new Ktx2RequestLoader(service, parser.options.manager, request, pool, onError)
      : undefined
    // 只作身份标记，GLTFLoader 的 loadImageSource 由下方请求隔离实现接管。
    const marker = adapter as unknown as KTX2Loader
    const sourceCache = new Map<number, Promise<THREE.CompressedTexture>>()
    const textureCache = new Map<string, THREE.Texture>()
    const loadImageSource = parser.loadImageSource.bind(parser)
    parser.loadImageSource = (sourceIndex, imageLoader) => {
      if (!adapter || imageLoader !== marker) return loadImageSource(sourceIndex, imageLoader)
      const cached = sourceCache.get(sourceIndex)
      if (cached) return cached.then((texture) => adapter!.track(texture.clone()))
      const image = parser.json.images?.[sourceIndex]
      const promise = Promise.resolve()
        .then(() => {
          if (!image) throw new Error(`KTX2 source=${sourceIndex} 不存在`)
          const source =
            image.bufferView !== undefined
              ? (parser.getDependency('bufferView', image.bufferView) as Promise<ArrayBuffer>)
              : typeof image.uri === 'string'
                ? THREE.LoaderUtils.resolveURL(image.uri, parser.options.path)
                : undefined
          if (source === undefined)
            throw new Error(`KTX2 source=${sourceIndex} 缺少 URI/bufferView`)
          return adapter!.load(source)
        })
        .then((texture) => {
          texture.userData.mimeType = 'image/ktx2'
          return texture
        })
        .catch((error) => {
          onError(error)
          throw error
        })
      sourceCache.set(sourceIndex, promise)
      return promise
    }
    const assignTexture = parser.assignTexture.bind(parser)
    parser.assignTexture = (params, name, info, colorSpace) => {
      if (!adapter || !parser.json.textures?.[info.index]?.extensions?.[EXTENSION])
        return assignTexture(params, name, info, colorSpace)
      return parser
        .getDependency('texture', info.index)
        .then((source: THREE.CompressedTexture | null) => {
          if (!source) throw new Error(`KTX2 texture=${info.index} 返回空贴图`)
          const transform = info.extensions?.KHR_texture_transform
          const key = `${source.uuid}:${colorSpace ?? THREE.NoColorSpace}:${info.texCoord ?? 0}:${JSON.stringify(transform)}`
          let texture = textureCache.get(key)
          if (!texture) {
            texture = adapter!.track(source.clone())
            texture.colorSpace = colorSpace ?? THREE.NoColorSpace
            texture.channel = info.texCoord ?? 0
            if (transform) {
              const extension = parser.extensions.KHR_texture_transform
              texture = adapter!.track<THREE.Texture>(extension.extendTexture(texture, transform))
            }
            texture.generateMipmaps = false
            if (texture.mipmaps.length === 1) {
              texture.minFilter = [
                THREE.NearestFilter,
                THREE.NearestMipmapNearestFilter,
                THREE.NearestMipmapLinearFilter,
              ].includes(texture.minFilter as typeof THREE.NearestFilter)
                ? THREE.NearestFilter
                : THREE.LinearFilter
            }
            textureCache.set(key, texture)
            parser.associations.set(texture, { textures: info.index })
          }
          params[name] = texture
          return texture
        })
        .catch((error: unknown) => {
          onError(error)
          throw error
        })
    }
    return {
      // 同名覆盖 Three.js 内置扩展，缺少可选 fallback 的情况也明确失败。
      name: EXTENSION,
      beforeRoot: () => {
        if (!adapter && parser.json.extensionsRequired?.includes(EXTENSION))
          throw new Error('KTX2 配置缺失：请配置 MeshPlugin.ktx2 或注入已初始化的转码器')
        return null
      },
      loadTexture: (index) => {
        const texture = parser.json.textures[index]
        const basis = texture.extensions?.[EXTENSION]
        if (!basis) return null
        if (!adapter) {
          const fallback = parser.json.images?.[texture.source]
          if (
            !fallback ||
            fallback.mimeType === 'image/ktx2' ||
            (!fallback.uri && fallback.bufferView === undefined)
          )
            throw new Error(`KTX2 配置缺失且 texture=${index} 没有普通图片后备`)
          return null
        }
        if (!Number.isInteger(basis.source) || !parser.json.images?.[basis.source])
          throw new Error(`KTX2 texture=${index} 的扩展 source 无效`)
        return parser.loadTextureImage(index, basis.source, marker)
      },
      afterRoot: (gltf: GLTF) => {
        const used = new Set<THREE.Texture>()
        for (const scene of gltf.scenes)
          scene.traverse((object) => {
            const material = (object as THREE.Mesh).material
            for (const item of Array.isArray(material) ? material : material ? [material] : [])
              for (const texture of enumerateMaterialTextures(item)) used.add(texture)
          })
        const ktx2 = [...used].filter((texture) => texture.userData.mimeType === 'image/ktx2')
        if (ktx2.length > 0) {
          report = {
            textureCount: ktx2.length,
            encodedBytes: adapter?.encodedBytes ?? 0,
            transcodedBytes: adapter?.transcodedBytes ?? 0,
            outputBytes: ktx2.reduce((sum, texture) => sum + estimateThreeTextureBytes(texture), 0),
            rgba8FallbackCount: ktx2.filter((texture) => texture.format === THREE.RGBAFormat)
              .length,
            workerPeakBytes: null,
          }
          log.debug(
            `event=mesh.gltf.ktx2.summary textures=${report.textureCount} encodedBytes=${report.encodedBytes} outputBytes=${report.outputBytes} rgba8Fallback=${report.rgba8FallbackCount}`
          )
        }
        adapter?.finish(used)
        return null
      },
    }
  })
  return {
    dispose: () => adapter?.dispose(),
    wasDisposed: (texture) => adapter?.wasDisposed(texture) ?? false,
    getReport: () => report,
  }
}
